// AI Generated Test
import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { PageAccessibilityRepoService } from '@client/app/services/api/page-accessibility/page-accessibility-repo.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { PAGE_DETAILS_SECTION_CONSTANT } from '@client/app/components/pagedetails/details/sections/page-details-section.constant';

@Component({
    selector: 'page-details-dashboard-charts-accessibility',
    standalone: false,
    template: `
        <div class="row mt-4">
            <div class="offset-4 col-18 page-dashboard-doughnut-container">
                <mon-chart-doughnut
                        *ngIf="!loading"
                        [labels]="labels"
                        [data]="data"
                        [options]="options"
                        [colors]="colors">
                </mon-chart-doughnut>
            </div>
            <div class="col-26 pl-0">
                <notification-change [title]="'Accessibility issues' | translate"
                                     [digit]="page.accessibility_checks_with_errors_count"
                                     (click)="onClickHandler.emit({params: {target: pageDeatilsSectionAccessibility}})">
                    <div class="mt-3"
                         translate
                         [translate-args]="{issues: totalIssuesCount | monNumber}">
                         (<strong><i>[[issues]]</i></strong> unique problems)
                    </div>
                </notification-change>
                <div class="mt-3 pl-4 d-inline-flex align-items-center">
                    <ng-container *ngFor="let check of guidelineIds; let first = first">
                        <div *ngIf="check.guidelineId | accessibilityHasGuidelineId"
                         class="icon-circle border-transparent text-white mr-1 outer-l-smurf size-sm"
                         [ngClass]="check.className"
                         (click)="onClickHandler.emit({params: {target: pageDeatilsSectionAccessibility}})">
                            <span class="icon">
                                {{check.name}}
                            </span>
                        </div>
                        <div *ngIf="check.guidelineId | accessibilityHasGuidelineId"
                             (click)="onClickHandler.emit({params: {target: pageDeatilsSectionAccessibility}})">
                            {{ getChecksWithErrorsCount(check.abbr) | monNumber }}
                        </div>
                    </ng-container>
                </div>
            </div>
        </div>
    `,
})
export class PageDetailsDashboardChartsAccessibilityComponent implements OnInit {
    @Input() page: any;
    @Output() onClickHandler = new EventEmitter<any>();

    checks: any[] = [];
    totalIssuesCount: number = 0;
    colors: string[] = ['#4dc0bd', '#dcdcdc'];
    options: any = {};
    data: any[] = [];
    labels: string[] = ['', ''];
    loading: boolean = true;
    pageDeatilsSectionAccessibility: any;
    guidelineIds: any[] = [];
    totalcheckErrors: number = 0;

    constructor (
        private pageAccessibilityRepoService: PageAccessibilityRepoService,
        private pipeService: PipeService,
    ) {}

    ngOnInit (): void {
        this.checks = [];
        this.totalIssuesCount = 0;
        this.colors = ['#4dc0bd', '#dcdcdc'];
        this.options = {};
        this.data = [];
        this.labels = ['', ''];
        this.loading = true;

        this.getChecks().then((checks: any) => {
            this.checks = checks;
            this.setupChecksCount();
            this.setupDoughnut();
        }, () => {
            // Handle error silently
        }).finally(() => {
            this.loading = false;
        });
        this.pageDeatilsSectionAccessibility = PAGE_DETAILS_SECTION_CONSTANT.ACCESSIBILITY;
    }

    private getChecks (): Promise<any> {
        const params = {
            page_size: 0,
        };
        return this.pageAccessibilityRepoService.getAllChecks(this.page.id, params);
    }

    private setupDoughnut (): void {
        const totalCheckCount = this.checks.reduce((num, check) => {
            if (!check.ignored && !check.ignored_on_page) {
                num++;
            }
            return num;
        }, 0);

        this.data = [totalCheckCount - this.totalcheckErrors, this.totalcheckErrors];
        const percentage = (this.totalcheckErrors / totalCheckCount) * 100;
        this.options = {
            centerText: {
                percentageLabel: this.pipeService.get('monNumber')((100 - percentage) / 100, '0.[00] %'),
            },
            cutoutPercentage: 80,
            tooltips: {
                enabled: totalCheckCount > 0,
                callbacks: {
                    beforeLabel: () => {
                        return '';
                    },
                    label: (tooltipItem: any, data: any) => {
                        return this.pipeService.get('monNumber')(
                            data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index],
                            '0,0',
                        );
                    },
                },
            },
        };
    }

    private setupChecksCount (): void {
        this.guidelineIds = [
            {
                name: 'A',
                guidelineId: 'WCAG2-A',
                className: 'bubble-wcag-a',
                abbr: 'A',
            },
            {
                name: 'AA',
                guidelineId: 'WCAG2-AA',
                className: 'bubble-wcag-aa',
                abbr: 'AA',
            },
            {
                name: 'AAA',
                guidelineId: 'WCAG2-AAA',
                className: 'bubble-wcag-aaa',
                abbr: 'AAA',
            },
            {
                name: 'A',
                guidelineId: 'WCAG21-A',
                className: 'bubble-wcag-a',
                abbr: 'A',
            },
            {
                name: 'AA',
                guidelineId: 'WCAG21-AA',
                className: 'bubble-wcag-aa',
                abbr: 'AA',
            },
            {
                name: 'AAA',
                guidelineId: 'WCAG21-AAA',
                className: 'bubble-wcag-aaa',
                abbr: 'AAA',
            },
        ];

        this.totalcheckErrors = this.getChecksWithErrorsCount();
        this.totalIssuesCount = this.checks.reduce((num, check) => {
            if (!check.ignored && !check.ignored_on_page) {
                num += parseInt(check.errors_count);
            }
            return num;
        }, 0);
    }

    getChecksWithErrorsCount (abbr?: string): number {
        return this.checks.reduce((num, check) => {
            if (!check.ignored && !check.ignored_on_page && check.errors_count > 0 && this.usesAbbr(abbr, check)) {
                num++;
            }
            return num;
        }, 0);
    }

    private usesAbbr (abbr: string | undefined, check: any): boolean {
        if (typeof abbr !== 'string') {
            return true;
        }
        return check.issue_abbr === abbr;
    }
}
